import React from 'react';
import Select, {
  components,
  DropdownIndicatorProps,
  StylesConfig,
  SingleValue as SingleValueType,
  MultiValue,
  ActionMeta,
} from 'react-select';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Tooltip } from './Tooltip';

export interface SelectDropdownOption {
  label: React.ReactNode;
  value: string;
  count?: number;
  sortOrder?: 'asc' | 'desc';
  isDisabled?: boolean;
  reason?: string;
}

export interface SelectDropdownProps {
  options: SelectDropdownOption[];
  value: string;
  onChange: (value: string, sortOrder?: 'asc' | 'desc') => void;
  label?: React.ReactNode;
  searchPlaceholder?: string;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  variant?: 'black' | 'default' | 'transparent';
  showCount?: boolean;
  searchable?: boolean;
  searchTerm?: string;
  onSearchChange?: (value: string) => void;
  isSearchLoading?: boolean;
  menuPortalTarget?: HTMLElement;
  maxMenuHeight?: string;
  onMenuOpen?: () => void;
  onMenuClose?: () => void;
  borderColor?: string;
}

// Custom dropdown indicator with chevron icons
const DropdownIndicator = (
  props: DropdownIndicatorProps<SelectDropdownOption>
) => {
  const { selectProps } = props;
  const variant = (selectProps as any).variant || 'default';

  return (
    <components.DropdownIndicator {...props}>
      {props.selectProps.menuIsOpen ? (
        <ChevronUp
          size={18}
          className={variant === 'black' ? 'text-white' : 'text-gray-500'}
        />
      ) : (
        <ChevronDown
          size={18}
          className={variant === 'black' ? 'text-white' : 'text-gray-500'}
        />
      )}
    </components.DropdownIndicator>
  );
};

// Custom Option component to show counts
const Option = (props: any) => {
  const { showCount } = props.selectProps;
  const option = props.data as SelectDropdownOption;

  const optionContent = (
    <div
      style={{
        cursor: option.isDisabled ? 'not-allowed' : 'pointer',
        width: '100%',
      }}
    >
      {option.label}
      {showCount && typeof option.count === 'number'
        ? ` (${option.count})`
        : ''}
    </div>
  );

  return (
    <components.Option 
      {...props} 
      isDisabled={option.isDisabled}
    >
      {option.isDisabled && option.reason ? (
        <Tooltip 
          content={option.reason} 
          position="right" 
          size="sm"
          icon={optionContent}
          iconClassName="w-full"
        />
      ) : (
        optionContent
      )}
    </components.Option>
  );
};

// Custom SingleValue component to show counts in selected value
const SingleValue = (props: any) => {
  const { showCount } = props.selectProps;
  const option = props.data as SelectDropdownOption;

  return (
    <components.SingleValue {...props}>
      {option.label}
      {showCount && typeof option.count === 'number'
        ? ` (${option.count})`
        : ''}
    </components.SingleValue>
  );
};

export const SelectDropdown: React.FC<SelectDropdownProps> = ({
  options,
  value,
  onChange,
  onSearchChange,
  label = '',
  searchPlaceholder = 'Search...',
  placeholder = 'Select',
  disabled = false,
  className = '',
  variant = 'default',
  showCount = false,
  searchable = false,
  menuPortalTarget = typeof document !== 'undefined' ? document.body : undefined,
  maxMenuHeight = '18rem',
  onMenuOpen,
  onMenuClose,
  borderColor,
}) => {
  const [menuIsOpen, setMenuIsOpen] = React.useState(false);
  // Use null explicitly when no option is found to properly clear react-select
  const selectedOption = options.find(opt => opt.value === value) || null;

  // Change placeholder when menu is open (user clicked on dropdown)
  const dynamicPlaceholder =
    menuIsOpen && searchable ? searchPlaceholder : placeholder;

  // Get custom styles based on variant
  const getCustomStyles = (): StylesConfig<SelectDropdownOption> => {
    const baseStyles = {
      control: (base: any, state: any) => {
        let backgroundColor, textColor, borderRadius, padding;

        switch (variant) {
          case 'black':
            backgroundColor = '#000000';
            textColor = '#ffffff';
            borderRadius = '9999px';
            padding = '0.25rem 1rem';
            break;
          case 'transparent':
            backgroundColor = 'transparent';
            textColor = '#111827';
            borderRadius = '0.375rem';
            padding = '0.5rem 0.75rem';
            break;
          default:
            backgroundColor = 'rgb(var(--color-primary-600))';
            textColor = 'rgb(var(--color-gray-100))';
            borderRadius = '9999px';
            padding = '0.25rem 1rem';
        }

        return {
          ...base,
          backgroundColor,
          color: textColor,
          borderRadius,
          padding,
          border: variant === 'transparent' ? `1px solid ${borderColor || '#d1d5db'}` : 'none',
          minHeight: 'auto',
          boxShadow: 'none',
          cursor: 'pointer',
          opacity: disabled ? 0.5 : 1,
          '&:hover': {
            border: variant === 'transparent' ? `1px solid ${borderColor || '#d1d5db'}` : 'none',
          },
        };
      },
      singleValue: (base: any) => ({
        ...base,
        color:
          variant === 'black'
            ? '#ffffff'
            : variant === 'transparent'
            ? '#111827'
            : 'rgb(var(--color-gray-100))',
      }),
      input: (base: any) => ({
        ...base,
        color:
          variant === 'black'
            ? '#ffffff'
            : variant === 'transparent'
            ? '#111827'
            : 'rgb(var(--color-gray-100))',
        margin: 0,
        paddingTop: 0,
        paddingBottom: 0,
      }),
      placeholder: (base: any) => ({
        ...base,
        color:
          variant === 'black'
            ? '#ffffff'
            : variant === 'transparent'
            ? '#6b7280'
            : 'rgb(var(--color-gray-100))',
      }),
      menu: (base: any) => ({
        ...base,
        backgroundColor: variant === 'black' ? '#000000' : '#ffffff',
        border: variant === 'black' ? 'none' : '1px solid #d1d5db',
        marginTop: '0.25rem',
        zIndex: 9999,
      }),
      menuPortal: (base: any) => ({
        ...base,
        zIndex: 9999,
      }),
      menuList: (base: any) => ({
        ...base,
        maxHeight: maxMenuHeight,
        padding: 0,
      }),
      option: (base: any, state: any) => {
        const isDisabled = state.isDisabled;
        const isSelected = state.isSelected;
        
        // Base background color (ignore isFocused state, rely on CSS :hover instead)
        let bgColor;
        if (isSelected) {
          bgColor = '#d1d5db';
        } else {
          bgColor = variant === 'black' ? '#000000' : '#ffffff';
        }

        return {
          ...base,
          backgroundColor: bgColor,
          color: variant === 'black' ? '#ffffff' : '#111827',
          cursor: isDisabled ? 'not-allowed' : isSelected ? 'not-allowed' : 'pointer',
          padding: '0.5rem 1rem',
          opacity: isDisabled ? 0.5 : 1,
          transition: 'background-color 0.1s ease',
          '&:hover': {
            backgroundColor: isDisabled
              ? bgColor
              : isSelected
              ? '#d1d5db'
              : variant === 'black'
              ? '#1f2937'
              : '#e5e7eb',
          },
        };
      },
      dropdownIndicator: (base: any) => ({
        ...base,
        padding: '0',
        paddingRight: '0.75rem',
        color: variant === 'black' ? '#ffffff' : '#6b7280',
      }),
      indicatorSeparator: () => ({
        display: 'none',
      }),
      valueContainer: (base: any) => ({
        ...base,
        padding: '2px 8px',
      }),
    };

    return baseStyles;
  };

  const handleChange = (
    selected:
      | SingleValueType<SelectDropdownOption>
      | MultiValue<SelectDropdownOption>,
    actionMeta: ActionMeta<SelectDropdownOption>
  ) => {
    // Handle single select (not multi-select)
    if (selected && !Array.isArray(selected)) {
      const option = selected as SelectDropdownOption;
      if (option.value !== value) {
        onChange(option.value, option.sortOrder || 'asc');
      }
    }
  };

  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      {label && (
        <label className="text-xs font-medium text-gray-700 mb-1">
          {label}
        </label>
      )}
      <div className="relative w-full min-w-[140px] max-h-24">
        <Select<SelectDropdownOption>
          options={options}
          value={selectedOption}
          onChange={handleChange}
          placeholder={dynamicPlaceholder}
          isDisabled={disabled}
          isSearchable={searchable}
          isOptionDisabled={(option) => option.isDisabled || false}
          styles={getCustomStyles()}
          components={{
            DropdownIndicator,
            Option,
            SingleValue,
          }}
          onMenuOpen={() => {
            setMenuIsOpen(true);
            onMenuOpen?.();
          }}
          onMenuClose={() => {
            setMenuIsOpen(false);
            onMenuClose?.();
          }}
          onInputChange={onSearchChange}
          menuPortalTarget={menuPortalTarget}
          menuPlacement="auto"
          // @ts-ignore - Custom props for our custom components
          variant={variant}
          showCount={showCount}
        />
      </div>
    </div>
  );
};

export default SelectDropdown;
